import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { 
    Table, 
    Button, 
    Form, 
    Input, 
    Switch, 
    Space, 
    Alert, 
    Card, 
    Modal, 
    Typography, 
    Image, 
    Tag, 
    Popconfirm,
    message,
    Row,
    Col,
    Divider
} from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import ModernImageUpload from '../../UI/ModernImageUpload';

const { Title, Text } = Typography;

function AdminCategories() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [editingCategory, setEditingCategory] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();

    const load = async () => {
        try {
            setLoading(true);
            const res = await api.adminGetCategories({ paginate: 0 });
            setCategories(res.data);
        } catch (e) {
            setError('Failed to load categories');
            message.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCreate = () => {
        setEditingCategory(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        form.setFieldsValue({
            name: category.name,
            description: category.description,
            is_active: category.is_active,
            image: category.image ? [{ 
                id: category.image_id,
                url: category.image,
                name: category.name,
                alt_text: category.name
            }] : []
        });
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        try {
            setSaving(true);
            
            // Extract image data
            const imageData = values.image && values.image.length > 0 ? values.image[0] : null;
            
            const categoryData = {
                name: values.name,
                description: values.description,
                is_active: values.is_active,
                image: imageData?.url || null,
                image_id: imageData?.id || null
            };

            if (editingCategory) {
                await api.adminUpdateCategory(editingCategory.id, categoryData);
                message.success('Category updated successfully!');
            } else {
                await api.adminCreateCategory(categoryData);
                message.success('Category created successfully!');
            }

            form.resetFields();
            setModalVisible(false);
            await load();
        } catch (e) {
            setError('Failed to save category');
            message.error('Failed to save category');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (category) => {
        try {
            await api.adminUpdateCategory(category.id, { is_active: !category.is_active });
            message.success('Category status updated!');
            await load();
        } catch (e) {
            message.error('Failed to update category status');
        }
    };

    const removeCategory = async (id) => {
        try {
            await api.adminDeleteCategory(id);
            message.success('Category deleted successfully!');
            await load();
        } catch (e) {
            message.error('Failed to delete category');
        }
    };

    const columns = [
        {
            title: 'Image',
            key: 'image',
            width: 100,
            render: (_, record) => (
                <div className="w-16 h-16">
                    {record.image ? (
                        <Image
                            src={record.image}
                            alt={record.name}
                            className="w-full h-full object-cover rounded"
                            fallback="/images/placeholder.svg"
                        />
                    ) : (
                        <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                            <Text type="secondary" className="text-xs">No Image</Text>
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div>
                    <Text strong>{text}</Text>
                    {record.description && (
                        <div>
                            <Text type="secondary" className="text-sm">{record.description}</Text>
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            width: 100,
            render: (_, record) => (
                <Tag color={record.is_active ? 'green' : 'red'}>
                    {record.is_active ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button 
                        type="text" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record)}
                        size="small"
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete Category"
                        description="Are you sure you want to delete this category?"
                        onConfirm={() => removeCategory(record.id)}
                        okText="Yes, Delete"
                        cancelText="Cancel"
                    >
                        <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />}
                            size="small"
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Manage Categories</Title>
                <Text type="secondary">
                    Create and manage food categories with images and descriptions.
                </Text>
            </div>

            {error && (
                <Alert 
                    type="error" 
                    message={error} 
                    className="mb-6" 
                    closable
                    onClose={() => setError('')}
                />
            )}

            <Card className="mb-6">
                <div className="flex justify-between items-center mb-4">
                    <Title level={4} className="!mb-0">Categories</Title>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        Add Category
                    </Button>
                </div>

                <Table 
                    columns={columns} 
                    dataSource={categories} 
                    rowKey="id" 
                    loading={loading} 
                    pagination={{ 
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} categories`,
                    }} 
                    scroll={{ x: 800 }}
                />
            </Card>

            <Modal
                title={editingCategory ? 'Edit Category' : 'Create Category'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ is_active: true }}
                >
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item
                                label="Category Image"
                                name="image"
                                rules={[{ required: true, message: 'Please upload a category image' }]}
                            >
                                <ModernImageUpload
                                    currentImage={form.getFieldValue('image')}
                                    onUpload={async (file) => {
                                        form.setFieldValue('image', file);
                                        return { file_path: URL.createObjectURL(file) };
                                    }}
                                    onRemove={() => {
                                        form.setFieldValue('image', null);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        
                        <Col span={24}>
                            <Form.Item
                                label="Category Name"
                                name="name"
                                rules={[{ required: true, message: 'Please enter category name' }]}
                            >
                                <Input placeholder="Enter category name" />
                            </Form.Item>
                        </Col>
                        
                        <Col span={24}>
                            <Form.Item
                                label="Description"
                                name="description"
                            >
                                <Input.TextArea 
                                    placeholder="Enter category description"
                                    rows={3}
                                />
                            </Form.Item>
                        </Col>
                        
                        <Col span={24}>
                            <Form.Item
                                label="Status"
                                name="is_active"
                                valuePropName="checked"
                            >
                                <Switch 
                                    checkedChildren="Active" 
                                    unCheckedChildren="Inactive" 
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <div className="flex justify-end space-x-2">
                        <Button onClick={() => setModalVisible(false)}>
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={saving}
                        >
                            {editingCategory ? 'Update Category' : 'Create Category'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}

export default AdminCategories;
